import { Vector3 } from 'three';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { Halfedge, Vertex } from '@/thirdPartyLibraries/three-mesh-halfedge/src';

/**
 * Builds 'n+1' lateral rings by duplicating the current ordered border loop,
 * offsetting it, and stitching quads (two triangles per rim edge).
 *
 * Notes:
 *  - We reuse your proven triangle patterns, but:
 *    • resolve the old rim halfedge in a robust way (v1→v2 or v2→v1),
 *    • never pass custom renderId (let DS manage nextRenderId),
 *    • guard against missing edges (shouldn’t happen on a proper border loop).
 *  - We recompute border vertices each iteration (as your original did) to keep
 *    the active loop consistent even if local topology changes.
 */

export function fillSides(
  mesh: HalfEdgeMesh,
  n: number,
  top = false,
  opts?: { direction?: Vector3; step?: number; minClear?: number }
): void {
  const ds = mesh.halfEdges;

  // Heuristic distances derived from mesh scale
  const meanEdge = Math.max(ds.getMeanEdgeLength?.() ?? 0.05, 1e-4);
  const ringStepDefault = 0.4 * meanEdge;
  const minClearDefault = 0.25 * meanEdge;
  const explicitDir = opts?.direction?.clone().normalize();
  const ringStep = opts?.step ?? ringStepDefault;
  const minClear = opts?.minClear ?? minClearDefault;

  const centroid = mesh.getCentroid();

  for (let ring = 0; ring <= n; ring++) {
    // 1) Take the current ordered border loop
    const border = mesh.getConnectedBorderVertices();
    if (!border || border.length < 3) break;

    // 2) Build next ring
    const newRing: Vertex[] = [];
    for (let i = 0; i < border.length; i++) {
      const v = border[i];
      const p = v.position.clone();

      if (explicitDir) {
        // Push in explicit direction only (e.g., strict -Y)
        p.addScaledVector(explicitDir, ringStep);
      } else {
        // Local-frame outward growth
        const prev = border[(i - 1 + border.length) % border.length];
        const next = border[(i + 1) % border.length];
        const tangent = new Vector3().subVectors(next.position, prev.position).normalize();
        let normal = v.computeVertexNormal();
        if (!isFinite(normal.x) || !isFinite(normal.y) || !isFinite(normal.z) || normal.lengthSq() < 1e-12) {
          normal = new Vector3(0, top ? 1 : -1, 0);
        }
        let side = new Vector3().crossVectors(tangent, normal);
        if (side.lengthSq() < 1e-12) side = new Vector3().subVectors(v.position, centroid);
        side.normalize();
        const radial = new Vector3().subVectors(v.position, centroid).normalize();
        if (side.dot(radial) < 0) side.negate();
        const shapeBias = 0.05 * meanEdge * (top ? +1 : -1);
        p.addScaledVector(side, ringStep).addScaledVector(normal, shapeBias);
      }

      // Shrinkwrap/clearance against original geometry to avoid overlaps
      const bt: any = (mesh.geometry as any).boundsTree;
      if (bt?.closestPointToPoint) {
        const hit: any = {};
        bt.closestPointToPoint(p, hit);
        if (hit.point) {
          const cp = hit.point as Vector3;
          if (explicitDir) {
            const toSurface = new Vector3().subVectors(p, cp);
            const along = toSurface.dot(explicitDir);
            if (!isFinite(along) || along < minClear) {
              p.copy(cp).addScaledVector(explicitDir, minClear);
            }
          } else {
            // local-frame case
            const prev = border[(i - 1 + border.length) % border.length];
            const next = border[(i + 1) % border.length];
            const tangent = new Vector3().subVectors(next.position, prev.position).normalize();
            let normal = v.computeVertexNormal();
            if (!isFinite(normal.x) || !isFinite(normal.y) || !isFinite(normal.z) || normal.lengthSq() < 1e-12) {
              normal = new Vector3(0, top ? 1 : -1, 0);
            }
            let side = new Vector3().crossVectors(tangent, normal);
            if (side.lengthSq() < 1e-12) side = new Vector3().subVectors(v.position, centroid);
            side.normalize();
            const toSurface = new Vector3().subVectors(p, cp);
            const along = toSurface.dot(side);
            if (!isFinite(along) || along < minClear) {
              p.copy(cp).addScaledVector(side, minClear);
            }
          }
        }
      }

      newRing.push(ds.addVertex(p, /*renderId*/ undefined, v.color));
    }

    // 3) Stitch quads along the rim using two triangles per segment
    let previousEdge = ds.addEdge(border[0], newRing[0]);
    for (let i = 0; i < border.length; i++) {
      const i2 = i === border.length - 1 ? 0 : i + 1;
      const v1 = border[i];
      const v2 = border[i2];
      const n1 = newRing[i];
      const n2 = newRing[i2];

      let v1Edge: Halfedge | null = v1.getHalfedgeToVertex(v2);
      if (!v1Edge) {
        const back = v2.getHalfedgeToVertex(v1);
        v1Edge = back ? back.twin : null;
      }
      if (!v1Edge) continue;

      const n1_to_v2 = ds.addEdge(n1, v2);

      if (v1Edge.face === null) {
        ds.addFace([v1Edge, n1_to_v2.twin, previousEdge.twin]);
      } else {
        ds.addFace([v1Edge.twin, previousEdge, n1_to_v2]);
      }

      const v2_to_n2 = ds.addEdge(v2, n2);
      const n2_to_n1 = ds.addEdge(n2, n1);

      if (n1_to_v2.face === null) {
        ds.addFace([n1_to_v2, v2_to_n2, n2_to_n1]);
      } else {
        ds.addFace([n1_to_v2.twin, n2_to_n1.twin, v2_to_n2.twin]);
      }

      previousEdge = v2_to_n2;
    }

    mesh.calculateBorderVertices();
  }
}
